<script>
const VALID_DENSITIES = [
  'compact',
  'cozy',
  'comfortable'
]

export default {
  name: 'Datatable',
  components: {
    Draggable: () => import('vuedraggable'),
    InfiniteScroll: () => import('@/components/general/InfiniteScroll')
  },

  props: {
    items: {
      type: Array,
      default () {
        return []
      }
    },

    title: {
      type: String,
      default: null
    },

    drag: {
      type: Boolean,
      default: false
    },

    hasFade: {
      type: Boolean,
      default: false
    },

    hasInfiniteScroll: {
      type: Boolean,
      default: false
    },

    card: {
      type: Boolean,
      default: false
    },

    dark: {
      type: Boolean,
      default: false
    },

    light: {
      type: Boolean,
      default: false
    },

    hideHorizontalLine: {
      type: Boolean,
      default: false
    },

    isLoadingMore: {
      type: Boolean,
      default: false
    },

    emptyMessage: {
      type: String,
      default: null
    },

    density: {
      type: String,
      default: 'cozy',
      validator: value => VALID_DENSITIES.includes(value)
    },

    zebra: {
      type: Boolean,
      default: false
    },

    loading: {
      type: Boolean,
      default: false
    },

    skeletonRows: {
      type: Number,
      default: 5
    }
  },

  data () {
    return {
      tbodyEl: null
    }
  },

  computed: {
    dragOptions () {
      return {
        animation: 200,
        group: 'description',
        disabled: !this.drag,
        ghostClass: 'ghost'
      }
    },

    wrapperClass () {
      return [
        `density-${this.density}`,
        {
          'theme-dark': this.dark,
          'theme-light': this.light,
          'datatable-wrapper-card': this.card,
          'is-card': this.card,
          'is-zebra': this.zebra,
          'is-loading': this.loading,
          'hide-h-line': this.hideHorizontalLine
        }
      ]
    },

    isEmpty () {
      return !this.loading && (!this.items || this.items.length === 0)
    },

    emptyText () {
      if (this.emptyMessage === 'no_result') {
        return this.$t('global:datatable.no.result')
      }

      if (this.emptyMessage) {
        return this.emptyMessage
      }

      return this.$t('global:datatable.no.result')
    },

    skeletonCells () {
      return Array.from({ length: this.skeletonRows })
    }
  },

  watch: {
    items: {
      deep: true,
      immediate: true,
      handler (value) {
        if (this.hasInfiniteScroll && !this.isLoadingMore && value.length < 5) {
          this.$emit('loadMore')
        }
      }
    }
  },

  mounted () {
    if (this.hasInfiniteScroll && this.$refs.datatable) {
      this.tbodyEl = this.$refs.datatable
      this.tbodyEl.addEventListener('scroll', this.handleScroll, { passive: true })
    }
  },

  beforeDestroy () {
    if (this.tbodyEl) {
      this.tbodyEl.removeEventListener('scroll', this.handleScroll)
      this.tbodyEl = null
    }
  },

  methods: {
    handleScroll (event) {
      if (this.isLoadingMore) return

      const tbody = event.target
      const reachedBottom = tbody.scrollTop >= (tbody.scrollHeight - tbody.offsetHeight)

      if (reachedBottom) {
        this.$emit('loadMore')
      }
    }
  }
}
</script>

<template>
  <div
    class="datatable-wrapper"
    :class="wrapperClass"
  >
    <span
      v-if="title"
      class="datatable-title"
    >{{ title }}</span>
    <div
      v-if="$slots['header-actions']"
      class="datatable-toolbar"
    >
      <slot name="header-actions" />
    </div>
    <div class="datatable-inner">
      <table class="datatable">
        <thead v-if="$slots.thead">
          <slot name="thead" />
        </thead>
        <slot name="tsearch" />
        <tbody ref="datatable">
          <template v-if="loading && (!items || items.length === 0)">
            <tr
              v-for="i in skeletonCells.length"
              :key="`skel-${i}`"
              class="tr-skeleton"
            >
              <td
                class="td td-skeleton"
                colspan="100"
              >
                <span class="datatable-skeleton-bar" />
              </td>
            </tr>
          </template>

          <Draggable
            v-else-if="drag && !isEmpty"
            class="drag-container"
            :list="items"
            v-bind="dragOptions"
          >
            <template v-if="hasFade">
              <TransitionGroup name="fade">
                <slot
                  v-for="(item, index) in items"
                  name="tbody"
                  :item="item"
                  :index="index"
                />
              </TransitionGroup>
            </template>

            <template v-else>
              <slot
                v-for="(item, index) in items"
                name="tbody"
                :item="item"
                :index="index"
              />
            </template>
          </Draggable>

          <template v-else>
            <template v-if="hasFade">
              <TransitionGroup name="fade">
                <slot
                  v-for="(item, index) in items"
                  name="tbody"
                  :item="item"
                  :index="index"
                />
              </TransitionGroup>
            </template>

            <template v-else>
              <slot
                v-for="(item, index) in items"
                name="tbody"
                :item="item"
                :index="index"
              />
            </template>
          </template>

          <tr
            v-if="isEmpty"
            class="tr-empty"
          >
            <td
              class="td td-empty"
              colspan="100"
            >
              <slot name="empty">
                <div class="datatable-empty">
                  <span class="datatable-empty-text">{{ emptyText }}</span>
                </div>
              </slot>
            </td>
          </tr>

          <Transition name="fade">
            <tr
              v-if="hasInfiniteScroll && isLoadingMore"
              class="tr-loadmore"
            >
              <td
                class="td td-loadmore"
                colspan="100"
              >
                <InfiniteScroll />
              </td>
            </tr>
          </Transition>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style src="@/assets/styles/themes/default/datatable.css"></style>
